import React from 'react';
import { Surface, MetricTile } from '../design/primitives';
import ProgressiveCard from './ProgressiveCard';

/**
 * BentoResults — responsive bento layout for the results surface.
 * ─────────────────────────────────────────────────────────────────────────────
 * Pure layout engine. Arranges a full-width hero ProgressiveCard plus an ordered
 * list of tiles in a 4-column grid that collapses 4 → 2 → 1 (.vc-bento in
 * public/design/v2/site.css). Owns NO chart code and NO data transforms — the
 * caller passes pre-rendered Recharts elements (and the depth-3 detail) as nodes.
 *
 * Props:
 *   depth   — 1|2|3, passed straight through to the hero card
 *   hero    — { items, summary, onDrill } for the winning option
 *   tiles      — ordered tile descriptors (capped at 8):
 *                  { key, kind:'metric',  label, value, sub?, span? }
 *                  { key, kind:'chart',   title, node, span? }
 *                  { key, kind:'custom',  node, span?, bare? }
 *   detail     — ReactNode | ReactNode[] revealed only at depth === 3
 *   bareDetail — skip the outer Surface wrapper around `detail` (default
 *                false). Use when `detail` already brings its own card chrome.
 *
 * v2 ("Tell-tale"): metric tiles are readings, everything else a card. Two font
 * weights in body copy, no shadows. Cells get min-width:0 so a Recharts
 * ResponsiveContainer can shrink inside the grid track.
 * ─────────────────────────────────────────────────────────────────────────
 */

const MAX_TILES = 8;

function ChartTile({ title, node }) {
  return (
    <Surface className="vc-chart">
      {title && <h2 className="vc-chart__title">{title}</h2>}
      <div className="vc-chart__plot">{node}</div>
    </Surface>
  );
}

function renderTile(tile) {
  switch (tile.kind) {
    case 'metric':
      return (
        <MetricTile label={tile.label} value={tile.value} sub={tile.sub} />
      );
    case 'chart':
      return <ChartTile title={tile.title} node={tile.node} />;
    case 'custom':
      return tile.bare ? (
        tile.node
      ) : (
        <Surface>{tile.node}</Surface>
      );
    default:
      throw new Error(`BentoResults: unknown tile kind "${tile.kind}"`);
  }
}

export default function BentoResults({
  depth,
  hero,
  tiles = [],
  detail,
  bareDetail = false,
}) {
  let shown = tiles;
  if (tiles.length > MAX_TILES) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `BentoResults: ${tiles.length} tiles passed; capping at ${MAX_TILES}. ` +
          'Keep the inventory to hero + 8 tiles.',
      );
    }
    shown = tiles.slice(0, MAX_TILES);
  }

  return (
    <div>
      <div className="vc-bento">
        {/* Hero — full-width band */}
        <div className="vc-bento__cell vc-bento__cell--band">
          <ProgressiveCard
            items={hero.items}
            summary={hero.summary}
            depth={depth}
            featured
            expandable
            onDrill={hero.onDrill}
          />
        </div>

        {shown.map((tile) => {
          /* Metric tiles are single-column (they form the tidy 4-across row);
             every other tile is a full-width band. */
          const isBand = tile.kind !== 'metric';
          return (
            <div
              key={tile.key}
              className={`vc-bento__cell${isBand ? ' vc-bento__cell--band' : ''}`}
            >
              {renderTile(tile)}
            </div>
          );
        })}
      </div>

      {depth === 3 && detail != null && (() => {
        const body = Array.isArray(detail)
          ? detail.map((node, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} style={{ marginTop: i === 0 ? 0 : 16 }}>
                {node}
              </div>
            ))
          : detail;
        return (
          <div style={{ marginTop: 16 }}>
            {bareDetail ? body : <Surface>{body}</Surface>}
          </div>
        );
      })()}
    </div>
  );
}
